import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Row, Col, Table, Alert, message, Form, DatePicker } from "antd";
import { AUTH_HEADER_NAME } from "common/apis";
import { AppPageHeader } from "components/AppPageHeader";
import { AppUpload } from "components/AppUpload";
import { useAppSelector } from "hooks/useAppSelector";
import { PartnerVariant, usePartnerServicesConfig } from "hooks/usePartnerServicesConfig";
import { generateHeaders } from "common/utils";
import { ColumnsType } from "antd/lib/table";
import { useDocumentTitle } from "hooks/usePageTitle";

const UploadGigPayoutsPage = ({ variant = "ppd" }: { variant?: PartnerVariant }) => {
  useDocumentTitle("Upload Gig Payouts");
  const { authToken } = useAppSelector((state) => state.app.user);
  const config = usePartnerServicesConfig(variant);
  const {
    dispatch,
    processingGigPayouts,
    uploadGigPayoutsUrl,
    partnerRoutes,
    setGigPayoutsAction,
    removeGigPayoutsAction,
  } = config;

  const navigate = useNavigate();
  const [hasErrorRows, setHasErrorRows] = useState(false);
  const [month, setMonth] = useState<string>();
  const [form] = Form.useForm();

  useEffect(() => {
    const { timeUploaded } = processingGigPayouts;
    if (timeUploaded) {
      const uploadedTime = new Date(timeUploaded).getTime();
      const currentTime = new Date().getTime();
      const minsPassed = (currentTime - uploadedTime) / 1000 / 60;
      if (minsPassed >= 15) {
        dispatch(removeGigPayoutsAction());
      }
    }
    if (processingGigPayouts?.key) {
      navigate(`/${partnerRoutes.SAVE_GIG_PAYOUTS}`);
    }
    return () => { message.destroy(); };
  }, [processingGigPayouts, navigate, dispatch, removeGigPayoutsAction, partnerRoutes]);

  useEffect(() => {
    if (processingGigPayouts?.failedRows?.length) {
      setHasErrorRows(true);
    }
  }, [processingGigPayouts?.failedRows]);

  const headers: { [name: string]: string } = {
    ...(authToken && { [AUTH_HEADER_NAME]: authToken }),
  };

  const onUploaded = (data: any) => dispatch(setGigPayoutsAction(data));
  const onUploading = () => dispatch(removeGigPayoutsAction());

  const { headerMapping } = processingGigPayouts;
  const headerMappingWithError = headerMapping
    ? headerMapping + ",actualEarnings:Actual Earnings,differenceAmount:Difference Amount,errors:Errors"
    : "";

  const generateMemoizedHeaders = useMemo(
    () => generateHeaders(headerMappingWithError),
    [headerMappingWithError]
  );

  const tableHeaders = generateMemoizedHeaders || {};
  const successRows = processingGigPayouts?.successRows || [];
  const failedRows = processingGigPayouts?.failedRows || [];
  const successData = successRows.map((row, i) => ({ ...row, key: `data-success-${i}` }));
  const failedData = failedRows.map((row, i) => ({ ...row, key: `data-failed-${i}` }));
  const data = [...failedData, ...successData];

  const columns: ColumnsType<any & { key: string }> = [
    ...Object.keys(tableHeaders).map((key) => ({
      key,
      title: tableHeaders[key],
      dataIndex: key,
      render: (text: string, record: any) => {
        if (key === "errors") {
          return <>{record.errors?.map((e: any) => e.message).join(", ")}</>;
        }
        return <>{text}</>;
      },
    })),
  ];

  const addBorderToErrorRows = (record: any) =>
    record.errors?.length ? "error-row" : "";

  return (
    <>
      <Row>
        <Col>
          <AppPageHeader title="Upload Gig Payouts" />
        </Col>
      </Row>
      <Row>
        <Form style={{ width: "80%" }} form={form} layout="inline">
          <Form.Item
            label="Month"
            name="month"
            required
            rules={[{ required: true, message: "Month is required" }]}
          >
            <DatePicker
              picker="month"
              format="YYYY-MM"
              onChange={(date) => setMonth(date ? date.format("YYYY-MM") : undefined)}
            />
          </Form.Item>
        </Form>
      </Row>

      {month ? (
        <Row style={{ marginTop: "1rem" }}>
          <Col lg={16} xs={24}>
            <AppUpload
              uploadUrl={uploadGigPayoutsUrl}
              headers={headers}
              onUploaded={onUploaded}
              onUploading={onUploading}
              hasErrorRows={hasErrorRows}
              data={{ month }}
            />
          </Col>
        </Row>
      ) : (
        <Row style={{ marginTop: "1rem" }}>
          <Col>Select a month to upload a file.</Col>
        </Row>
      )}

      {!!Object.keys(processingGigPayouts).length && !processingGigPayouts.key && (
        <>
          <Row>
            <Col lg={16} xs={24}>
              <Alert
                type="error"
                message="Errors in sheet data."
                description="Please correct errors and upload again."
                showIcon
                banner
              />
            </Col>
          </Row>
          <Row>
            <Table
              size="small"
              columns={columns}
              dataSource={data}
              rowClassName={addBorderToErrorRows}
              scroll={{ y: 500, x: "100vh" }}
              className="table-with-errors"
            />
          </Row>
        </>
      )}
    </>
  );
};

export default UploadGigPayoutsPage;
